import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogClose } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ArrowUpRight, ArrowRight, Copy, Calendar, Newspaper, MapPin, Clock, ClipboardList, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Users as UsersIcon, Crown, Star, Home as HomeIcon, Plane, ShieldCheck, PartyPopper, Check, X as XIcon, MessageSquare, Send, DollarSign, Image as ImageIco, ImageOff, Loader2, Gauge, Trophy, BarChart3, Cloud, Handshake, User as UserIcon, MessagesSquare, Package, Pin, Share2, Link2, Facebook, Instagram, MessageCircle, Mail, Shield } from "lucide-react";
import { useCardExpansion } from "@/hooks/useCardExpansion";
import { CardThumb } from "@/components/cards/CardThumb";
import PlayHQPreview from "@/components/PlayHQPreview";
import { BFVotingSection } from "@/components/BFVoting";
import StatsBoard from "@/components/StatsBoard";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Input } from "@/components/ui/input";
import EmojiPicker from "@/components/EmojiPicker";
import ReactionRow from "@/components/ReactionRow";
import { FlyerImage } from "@/components/PlayerProfileCard";
import { PushNotificationCard } from "@/components/PushNotificationCard";
import { InstallAppCard } from "@/components/InstallAppCard";
import { UpdateBanner } from "@/components/UpdateBanner";
import { ApkUpdateBanner } from "@/components/ApkUpdateBanner";
import { toast } from "sonner";

const ROUND_FULL_LABEL = { QF: "Quarter Final", SF: "Semi Final", PF: "Preliminary Final", GF: "Grand Final" };

// Sponsor tier badge colours (matches landing page)
const MEMBER_TIER_BADGES = {
  gold:   { label: "Gold",   bg: "bg-[#C4A24D]/90", text: "text-[#5C4A1E]", border: "border-[#C4A24D]/30" },
  silver: { label: "Silver", bg: "bg-[#8E929A]/85", text: "text-[#2A2D31]", border: "border-[#8E929A]/30" },
  bronze: { label: "Bronze", bg: "bg-[#A87B56]/85", text: "text-[#4A3422]", border: "border-[#A87B56]/30" },
};

function roundLabel(r) {
  if (!r) return "";
  if (/^\d+$/.test(r)) return `Round ${r}`;
  return ROUND_FULL_LABEL[r] || r;
}

function fmtDateRange(startISO, endISO) {
  if (!startISO) return "";
  try {
    const s = new Date(startISO);
    const opts = { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" };
    const sStr = s.toLocaleString(undefined, opts);
    if (endISO) {
      const e = new Date(endISO);
      const sameDay = s.toDateString() === e.toDateString();
      const eStr = sameDay
        ? e.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
        : e.toLocaleString(undefined, opts);
      return `${sStr} → ${eStr}`;
    }
    return sStr;
  } catch { return startISO; }
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

function fmtTime12(t) {
  if (!t) return "";
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const m = (mStr || "00").padStart(2, "0");
  if (Number.isNaN(h)) return t;
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${suffix}`;
}

// --- Landing-page-matching helpers ---
function isHappeningNow(ev) {
  if (!ev.start_at || !ev.end_at) return false;
  const now = new Date();
  return new Date(ev.start_at) <= now && now < new Date(ev.end_at);
}
function fmtEventDateRange(start, end) {
  if (!start) return "";
  try {
    const s = new Date(start);
    const sStr = s.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
    if (!end) return sStr;
    const e = new Date(end);
    if (s.toDateString() === e.toDateString()) return `${sStr} – ${e.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" })}`;
    return `${sStr} – ${e.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}`;
  } catch { return start; }
}
function fmtPrice(cents) {
  const n = Number(cents) || 0;
  if (n <= 0) return null;
  return `$${(n / 100).toLocaleString(undefined, { minimumFractionDigits: n % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
}

const KIND_CONFIG = {
  news:         { label: "News",         bg: "#4169E1" },
  announcement: { label: "Announcement", bg: "#E63946" },
  sponsorship:  { label: "Sponsorship",  bg: "#C4A24D" },
  player:       { label: "Player",       bg: "#2E8B57" },
  match:        { label: "Match",        bg: "#7C3AED" },
  club:         { label: "Club",         bg: "#0891B2" },
};
function getKindConfig(kind) {
  const key = (kind || "").toLowerCase().trim();
  return KIND_CONFIG[key] || KIND_CONFIG.news;
}
function NewsKindBadge({ kind, size = "md" }) {
  const cfg = getKindConfig(kind);
  const sizeClass = size === "sm"
    ? "px-1.5 py-0.5 text-[8px] tracking-[0.08em]"
    : "px-2.5 py-1 text-[10px] sm:text-[11px] tracking-[0.14em]";
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap font-bold uppercase text-white shadow-md max-w-full ${sizeClass}`}
      style={{ backgroundColor: cfg.bg, fontFamily: "'Inter', sans-serif" }}
      data-testid={`news-kind-badge-${(kind || "news").toLowerCase()}`}
    >
      {cfg.label}
    </span>
  );
}
function ScrollChevron() {
  const parentRef = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = parentRef.current?.parentElement;
    if (!el) return;
    const check = () => {
      const overflows = el.scrollHeight > el.clientHeight + 8;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
      setShow(overflows && !atBottom);
    };
    check();
    el.addEventListener("scroll", check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", check); ro.disconnect(); };
  }, []);
  if (!show) return <span ref={parentRef} className="hidden" />;
  return (
    <span ref={parentRef} className="sticky bottom-1 flex justify-center w-full animate-bounce pointer-events-none z-10">
      <ChevronDown className="h-5 w-5 text-muted-foreground" />
    </span>
  );
}

function MemberEventShareSheet({ ev }) {
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  if (!ev) return null;
  const shareUrl = `${window.location.origin}/api/share/event/${ev.id}`;
  const shareTitle = ev.title || "Check out this event";
  const shareText = ev.description ? `${shareTitle} — ${ev.description.slice(0, 100)}${ev.description.length > 100 ? "…" : ""}` : shareTitle;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { const ta = document.createElement("textarea"); ta.value = shareUrl; ta.style.position = "fixed"; ta.style.opacity = "0"; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(shareTitle);
  const encodedText = encodeURIComponent(shareText);
  const channels = [
    { id: "copy", label: copied ? "Copied!" : "Copy link", icon: copied ? Check : Link2, action: () => { copyLink(); }, highlight: copied },
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${encodedText}%0A%0A${encodedUrl}`, color: "text-[#25D366]" },
    { id: "facebook", label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, color: "text-[#1877F2]" },
    { id: "instagram", label: "Instagram", icon: Instagram, action: async () => { await copyLink(); const { toast: t } = await import("sonner"); t.info("Link copied — paste it in your Instagram story or DM"); }, color: "text-[#E4405F]" },
    { id: "x", label: "X", icon: XIcon, href: `https://x.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}` },
    { id: "email", label: "Email", icon: Mail, href: `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}` },
  ];
  return (
    <div className="pt-1" ref={menuRef}>
      <Button variant="outline" size="sm" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground min-h-[44px]" data-testid="member-event-share-btn" onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}>
        <Share2 className="h-4 w-4" /> Share event
      </Button>
      {menuOpen && (
        <div className="mt-2 rounded-lg border border-border bg-popover p-1.5 shadow-lg" data-testid="member-event-share-popover" onClick={(e) => e.stopPropagation()}>
          <div className="grid grid-cols-3 gap-1">
            {channels.map((ch) => { const Icon = ch.icon; const cls = `flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${ch.highlight ? "text-emerald-600 font-medium" : ch.color || "text-foreground"}`; if (ch.action) return (<button key={ch.id} onClick={ch.action} data-testid={`member-event-share-${ch.id}`} className={cls}><Icon className="h-3.5 w-3.5 shrink-0" />{ch.label}</button>); return (<a key={ch.id} href={ch.href} target="_blank" rel="noopener noreferrer" data-testid={`member-event-share-${ch.id}`} className={cls} onClick={() => setMenuOpen(false)}><Icon className="h-3.5 w-3.5 shrink-0" />{ch.label}</a>); })}
          </div>
        </div>
      )}
    </div>
  );
}

function MemberNewsShareSheet({ item }) {
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  if (!item) return null;
  const shareUrl = `${window.location.origin}/api/share/news/${item.id}`;
  const shareTitle = item.title || "Check out this article";
  const shareText = item.summary ? `${shareTitle} — ${item.summary.slice(0, 100)}${item.summary.length > 100 ? "…" : ""}` : shareTitle;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { const ta = document.createElement("textarea"); ta.value = shareUrl; ta.style.position = "fixed"; ta.style.opacity = "0"; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(shareTitle);
  const encodedText = encodeURIComponent(shareText);
  const channels = [
    { id: "copy", label: copied ? "Copied!" : "Copy link", icon: copied ? Check : Link2, action: () => { copyLink(); }, highlight: copied },
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${encodedText}%0A%0A${encodedUrl}`, color: "text-[#25D366]" },
    { id: "facebook", label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, color: "text-[#1877F2]" },
    { id: "instagram", label: "Instagram", icon: Instagram, action: async () => { await copyLink(); const { toast: t } = await import("sonner"); t.info("Link copied — paste it in your Instagram story or DM"); }, color: "text-[#E4405F]" },
    { id: "x", label: "X", icon: XIcon, href: `https://x.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}` },
    { id: "email", label: "Email", icon: Mail, href: `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}` },
  ];
  return (
    <div className="pt-1" ref={menuRef}>
      <Button variant="outline" size="sm" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground min-h-[44px]" data-testid="member-news-share-btn" onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}>
        <Share2 className="h-4 w-4" /> Share article
      </Button>
      {menuOpen && (
        <div className="mt-2 rounded-lg border border-border bg-popover p-1.5 shadow-lg" data-testid="member-news-share-popover" onClick={(e) => e.stopPropagation()}>
          <div className="grid grid-cols-3 gap-1">
            {channels.map((ch) => { const Icon = ch.icon; const cls = `flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${ch.highlight ? "text-emerald-600 font-medium" : ch.color || "text-foreground"}`; if (ch.action) return (<button key={ch.id} onClick={ch.action} data-testid={`member-news-share-${ch.id}`} className={cls}><Icon className="h-3.5 w-3.5 shrink-0" />{ch.label}</button>); return (<a key={ch.id} href={ch.href} target="_blank" rel="noopener noreferrer" data-testid={`member-news-share-${ch.id}`} className={cls} onClick={() => setMenuOpen(false)}><Icon className="h-3.5 w-3.5 shrink-0" />{ch.label}</a>); })}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Member Event Detail (matches landing EventDetailDialog exactly, + RSVP) ---
function MemberEventDetail({ ev, onClose, rsvpSaving, submitRsvp }) {
  const happeningNow = isHappeningNow(ev);
  const price = fmtPrice(ev.price_cents);
  const evImgRef = useRef(null);
  const [evImgW, setEvImgW] = useState(null);

  const measureEvImg = useCallback(() => {
    const img = evImgRef.current;
    if (!img || window.innerWidth < 640) return;
    const elW = img.clientWidth;
    const elH = img.clientHeight;
    const natW = img.naturalWidth;
    const natH = img.naturalHeight;
    if (natW && natH && elW && elH) {
      const elAspect = elW / elH;
      const imgAspect = natW / natH;
      const displayedW = imgAspect > elAspect ? elW : Math.round(elH * imgAspect);
      setEvImgW(displayedW);
    } else {
      setEvImgW(img.getBoundingClientRect().width);
    }
  }, []);
  useEffect(() => {
    window.addEventListener("resize", measureEvImg);
    return () => window.removeEventListener("resize", measureEvImg);
  }, [measureEvImg]);
  useEffect(() => { if (!ev) setEvImgW(null); }, [ev]);

  return (
    <>
      <DialogClose
        className="absolute top-2 right-2 z-50 p-1 bg-white/80 backdrop-blur-sm rounded-full sm:hidden"
        data-testid="member-event-detail-close"
        aria-label="Close"
      >
        <XIcon className="h-7 w-7 text-black" strokeWidth={3} />
      </DialogClose>

      <div className="flex flex-col max-h-[98dvh] sm:max-h-[95dvh] p-2 sm:p-4 cursor-pointer" onClick={() => onClose()}>
        {ev.image_url && (
          <img
            ref={evImgRef}
            onLoad={measureEvImg}
            src={ev.image_url}
            alt={ev.title}
            className="max-w-full max-h-[60dvh] sm:max-h-[60dvh] object-contain rounded-lg sm:mx-auto sm:block"
            data-testid="event-detail-image"
          />
        )}

        <div
          className="relative flex-shrink-0 sm:flex-shrink overflow-y-auto scrollbar-hide sm:max-h-[30dvh]"
          style={evImgW ? { maxWidth: evImgW, marginInline: "auto" } : undefined}
        >
          <div className="p-3 sm:p-4 space-y-2.5">
            {happeningNow && (
              <div className="inline-flex items-center gap-1.5 rounded-full bg-red-600/90 text-white text-xs sm:text-[10px] font-semibold uppercase tracking-[0.14em] px-2.5 py-1">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex rounded-full h-full w-full bg-white" />
                </span>
                Happening now
              </div>
            )}

            <h1
              className="text-xl sm:text-2xl font-bold tracking-tight leading-tight"
              style={{ fontFamily: "'Inter', sans-serif", color: "#000000" }}
              data-testid="event-detail-title"
            >
              {ev.title}
            </h1>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm sm:text-sm text-muted-foreground">
              <div className="inline-flex items-center gap-1.5">
                <Clock className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-[hsl(var(--accent))]" />
                {fmtEventDateRange(ev.start_at, ev.end_at)}
              </div>
              {ev.location && (
                <div className="inline-flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-[hsl(var(--accent))]" />
                  {ev.location}
                </div>
              )}
              {price && (
                <div className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                  {price} <span className="font-normal text-muted-foreground text-sm">AUD</span>
                </div>
              )}
              <div className="inline-flex items-center gap-1.5">
                <UsersIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-[hsl(var(--accent))]" />
                <span className="font-semibold text-foreground" data-testid="event-detail-attending-count">{ev.attending_count || 0}</span>
                attending
              </div>
            </div>

            {ev.description && (
              <p className="text-sm sm:text-sm text-foreground/80 leading-relaxed" data-testid="event-detail-description">
                {ev.description}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-1" onClick={(e) => e.stopPropagation()}>
              {ev.cta_url && (
                <a
                  href={ev.cta_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] px-5 py-2 min-h-[44px] text-sm font-semibold hover:opacity-90 transition-opacity"
                  data-testid="event-detail-cta"
                >
                  {ev.cta_label || "Learn more"} <ArrowUpRight className="h-4 w-4" />
                </a>
              )}
              <MemberEventShareSheet ev={ev} />
            </div>

            {/* RSVP controls — member-only */}
            <div className="pt-2 border-t border-border" onClick={(e) => e.stopPropagation()}>
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-3">Your RSVP</div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  disabled={rsvpSaving}
                  onClick={() => submitRsvp(ev.id, "attending")}
                  data-testid="event-rsvp-attending"
                  className={`h-11 px-5 rounded-full ${
                    ev.my_rsvp === "attending"
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-[hsl(var(--secondary))] text-foreground hover:bg-[hsl(var(--secondary))]/70"
                  }`}
                >
                  <Check className="h-4 w-4 mr-2" />
                  {ev.my_rsvp === "attending" ? "I'm attending ✓" : "I'm attending"}
                </Button>
                <Button
                  type="button"
                  disabled={rsvpSaving}
                  onClick={() => submitRsvp(ev.id, "cant_attend")}
                  data-testid="event-rsvp-cant-attend"
                  variant="outline"
                  className={`h-11 px-5 rounded-full ${
                    ev.my_rsvp === "cant_attend"
                      ? "border-red-500/50 bg-red-500/10 text-red-700 dark:text-red-300"
                      : ""
                  }`}
                >
                  <XIcon className="h-4 w-4 mr-2" />
                  {ev.my_rsvp === "cant_attend" ? "Can't attend ✓" : "Can't attend"}
                </Button>
              </div>
              {ev.my_rsvp && (
                <p className="text-[11px] text-muted-foreground mt-2">
                  Tap your current choice again to clear your RSVP.
                </p>
              )}
            </div>
          </div>
          <ScrollChevron />
        </div>
      </div>
    </>
  );
}

// --- Member News Detail (matches landing NewsArticleDialog exactly) ---
function MemberNewsDetail({ item, onClose }) {
  const newsImgRef = useRef(null);
  const [newsImgW, setNewsImgW] = useState(null);

  const measureNewsImg = useCallback(() => {
    const img = newsImgRef.current;
    if (!img || window.innerWidth < 640) return;
    const elW = img.clientWidth;
    const elH = img.clientHeight;
    const natW = img.naturalWidth;
    const natH = img.naturalHeight;
    if (natW && natH && elW && elH) {
      const elAspect = elW / elH;
      const imgAspect = natW / natH;
      const displayedW = imgAspect > elAspect ? elW : Math.round(elH * imgAspect);
      setNewsImgW(displayedW);
    } else {
      setNewsImgW(img.getBoundingClientRect().width);
    }
  }, []);
  useEffect(() => {
    window.addEventListener("resize", measureNewsImg);
    return () => window.removeEventListener("resize", measureNewsImg);
  }, [measureNewsImg]);
  useEffect(() => { if (!item) setNewsImgW(null); }, [item]);

  return (
    <>
      <DialogClose
        className="absolute top-2 right-2 z-50 p-1 bg-white/80 backdrop-blur-sm rounded-full sm:hidden"
        data-testid="member-news-detail-close"
        aria-label="Close"
      >
        <XIcon className="h-7 w-7 text-black" strokeWidth={3} />
      </DialogClose>

      <div className="flex flex-col max-h-[98dvh] sm:max-h-[95dvh] p-2 sm:p-4 cursor-pointer" onClick={() => onClose()}>
        {item.image_url ? (
          item.source_kind === "team_selection" && item.player_hotspots?.length > 0 ? (
            <FlyerImage
              item={item}
              imgRef={newsImgRef}
              onLoad={measureNewsImg}
              className="max-w-full max-h-[60dvh] object-contain rounded-lg sm:mx-auto sm:block sm:max-h-[75dvh]"
            />
          ) : (
            <img
              ref={newsImgRef}
              onLoad={measureNewsImg}
              src={item.image_url}
              alt={item.title}
              className="max-w-full max-h-[60dvh] object-contain rounded-lg sm:mx-auto sm:block sm:max-h-[75dvh]"
              data-testid="desktop-news-detail-image"
            />
          )
        ) : (
          <div className="flex items-center justify-center h-[min(200px,30dvh)] text-muted-foreground">
            <Newspaper className="h-16 w-16 opacity-50" />
          </div>
        )}

        <div
          className="relative flex-shrink-0 sm:flex-shrink overflow-y-auto scrollbar-hide sm:max-h-[30dvh]"
          style={newsImgW ? { maxWidth: newsImgW, marginInline: "auto" } : undefined}
        >
          <div className="p-3 sm:p-4 space-y-2.5">
            <NewsKindBadge kind={item.kind} />
            <h1
              className="text-xl sm:text-2xl font-bold tracking-tight leading-tight"
              style={{ fontFamily: "'Inter', sans-serif", color: "#000000" }}
              data-testid="desktop-news-detail-title"
            >
              {item.title}
            </h1>
            <div className="text-sm sm:text-sm text-muted-foreground">
              {item.published_at
                ? new Date(item.published_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
                : ""}
            </div>
            {item.summary && (
              <p className="text-sm sm:text-sm leading-relaxed text-foreground/80" data-testid="desktop-news-detail-summary">
                {item.summary}
              </p>
            )}
            {item.body && (
              <div className="text-sm sm:text-base text-foreground/80 whitespace-pre-wrap leading-relaxed" data-testid="desktop-news-detail-body">
                {item.body}
              </div>
            )}
            <MemberNewsShareSheet item={item} />
          </div>
          <ScrollChevron />
        </div>
      </div>
    </>
  );
}

/** Availability group card — collapses to a compact summary once the member has responded. */
function AvailabilityGroupCard({ group, dateLabel, hasResponded, isSaving, onSubmit }) {
  const [expanded, setExpanded] = useState(!hasResponded);

  // Auto-collapse after successful response
  useEffect(() => {
    if (hasResponded) setExpanded(false);
  }, [hasResponded]);

  const statusColors = {
    available: { bg: "bg-[hsl(142_42%_34%)]", text: "text-white", label: "Available", Icon: Check },
    reserve: { bg: "bg-[hsl(38_92%_45%)]", text: "text-white", label: "Reserve", Icon: Star },
    unavailable: { bg: "bg-[hsl(var(--destructive))]", text: "text-white", label: "Unavailable", Icon: XIcon },
  };
  const currentStatus = statusColors[group.my_status] || null;

  // Collapsed view — compact single row
  if (hasResponded && !expanded) {
    return (
      <Card
        className="overflow-hidden border border-[hsl(var(--border))] bg-[#faf8f5] cursor-pointer hover:border-foreground/30 transition-colors"
        data-testid={`availability-group-${group.key}`}
        onClick={() => setExpanded(true)}
      >
        <div className="px-4 py-2.5 flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Calendar className="h-3.5 w-3.5 text-[hsl(var(--accent))] shrink-0" />
            <span className="text-xs font-semibold truncate">{dateLabel}</span>
            <Badge className="text-[9px] font-semibold tracking-wider uppercase bg-[hsl(var(--secondary))] text-muted-foreground border-transparent shrink-0">
              {group.team_type}
            </Badge>
          </div>
          {currentStatus && (
            <Badge
              className={`text-[10px] font-semibold px-2 py-0.5 ${currentStatus.bg} ${currentStatus.text} border-transparent shrink-0`}
              data-testid={`availability-status-${group.key}`}
            >
              <currentStatus.Icon className="h-3 w-3 mr-1" />
              {currentStatus.label}
            </Badge>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        </div>
      </Card>
    );
  }

  // Expanded view — full card with fixture details and controls
  return (
    <Card
      className="overflow-hidden border border-[hsl(var(--border))] bg-[#faf8f5]"
      data-testid={`availability-group-${group.key}`}
    >
      {/* Card Header */}
      <div
        className={`px-4 py-3 border-b border-[#15307a] ${hasResponded ? "cursor-pointer" : ""}`}
        style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #2749C9 55%, #4169E1 100%)" }}
        onClick={hasResponded ? () => setExpanded(false) : undefined}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-white/80" />
            <span className="font-semibold text-sm text-white">{dateLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              className="text-[10px] font-semibold tracking-wider uppercase bg-white/20 text-white border-white/30"
              data-testid={`availability-team-type-${group.key}`}
            >
              {group.team_type}
            </Badge>
            {hasResponded && <ChevronUp className="h-4 w-4 text-white/60" />}
          </div>
        </div>
      </div>

      {/* Fixture list */}
      <div className="px-4 py-3 space-y-2">
        {group.fixtures.map((f) => (
          <div
            key={f.id}
            className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm py-1.5 border-b border-[hsl(var(--border))]/50 last:border-b-0"
            data-testid={`availability-fixture-${f.id}`}
          >
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {f.round ? ((/^\d+$/.test(f.round)) ? `R${f.round}` : f.round) : ""}
            </span>
            {f.home_logo_url && (
              <img src={f.home_logo_url} alt="" className="h-5 w-5 rounded-full object-cover shrink-0" />
            )}
            <span className="font-medium truncate text-foreground">
              {f.team_name}
            </span>
            <span className="text-xs text-muted-foreground">vs</span>
            {f.away_logo_url && (
              <img src={f.away_logo_url} alt="" className="h-5 w-5 rounded-full object-cover shrink-0" />
            )}
            <span className="font-medium truncate text-foreground">
              {f.opponent || "TBC"}
            </span>
            {f.home_away && (
              <span className="flex items-center text-xs text-muted-foreground">
                {f.home_away === "Home" ? <HomeIcon className="h-3 w-3 mr-0.5" /> : <Plane className="h-3 w-3 mr-0.5" />}
                {f.home_away}
              </span>
            )}
            {f.time && (
              <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" /> {fmtTime12(f.time)}
              </span>
            )}
            {f.venue && (
              <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
                <MapPin className="h-3 w-3" /> <span className="truncate max-w-[150px]">{f.venue}</span>
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Availability controls */}
      <div className="px-4 py-3 bg-[#f5f0e8]/50 border-t border-[hsl(var(--border))]">
        {group.my_status === "mixed" && (
          <p className="text-xs text-[hsl(var(--warning))] mb-2 font-medium">
            You have different responses for matches in this group — choose below to apply to all.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={isSaving}
            onClick={() => onSubmit(group, "available")}
            data-testid={`availability-btn-available-${group.key}`}
            className={`min-h-[44px] px-4 rounded-full transition-colors ${
              group.my_status === "available"
                ? "bg-[hsl(142_42%_34%)] hover:bg-[hsl(142_42%_30%)] text-white shadow-sm"
                : "bg-[hsl(var(--secondary))] text-foreground hover:bg-[hsl(var(--secondary))]/70 border border-[hsl(var(--border))]"
            }`}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-1.5" />
            )}
            Available
          </Button>
          <Button
            size="sm"
            disabled={isSaving}
            onClick={() => onSubmit(group, "reserve")}
            data-testid={`availability-btn-reserve-${group.key}`}
            className={`min-h-[44px] px-4 rounded-full transition-colors ${
              group.my_status === "reserve"
                ? "bg-[hsl(38_92%_45%)] hover:bg-[hsl(38_92%_40%)] text-white shadow-sm"
                : "bg-[hsl(var(--secondary))] text-foreground hover:bg-[hsl(var(--secondary))]/70 border border-[hsl(var(--border))]"
            }`}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Star className="h-4 w-4 mr-1.5" />
            )}
            Reserve
          </Button>
          <Button
            size="sm"
            disabled={isSaving}
            onClick={() => onSubmit(group, "unavailable")}
            data-testid={`availability-btn-unavailable-${group.key}`}
            className={`min-h-[44px] px-4 rounded-full transition-colors ${
              group.my_status === "unavailable"
                ? "bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90 text-white shadow-sm"
                : "bg-[hsl(var(--secondary))] text-foreground hover:bg-[hsl(var(--secondary))]/70 border border-[hsl(var(--border))]"
            }`}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <XIcon className="h-4 w-4 mr-1.5" />
            )}
            Unavailable
          </Button>
        </div>
        {group.my_status && group.my_status !== "mixed" && (
          <p className="text-xs text-muted-foreground mt-2">
            Currently: <span className="font-semibold capitalize">{group.my_status}</span> — tap a different option to change.
          </p>
        )}
      </div>
    </Card>
  );
}

export default function MemberDashboard() {
  const { user, isAdmin } = useAuth();
  const isPrimaryAdmin = user?.role === "primary_admin";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [rsvpSaving, setRsvpSaving] = useState(false);
  const [news, setNews] = useState([]);
  const eventCards = useCardExpansion();
  const newsCards = useCardExpansion();
  // Comments state
  const [unreadComments, setUnreadComments] = useState({ total: 0, per_fixture: {} });
  const [commentsByFixture, setCommentsByFixture] = useState({}); // { fixture_id: [comments...] }
  const [commentDraft, setCommentDraft] = useState({}); // { fixture_id: text }
  const [sendingComment, setSendingComment] = useState(null);
  const [replyTo, setReplyTo] = useState({}); // { fixture_id: parent_comment_id | null }
  const [chatNotifs, setChatNotifs] = useState([]);
  const [memberNotifs, setMemberNotifs] = useState([]);
  const [expandedNewsCards, setExpandedNewsCards] = useState(new Set());
  // Landing-style desktop sections state
  const [publicStats, setPublicStats] = useState(null);
  const [landing, setLanding] = useState(null);
  const desktopEventsScrollRef = useRef(null);
  const [desktopEventsEdge, setDesktopEventsEdge] = useState({ left: true, right: false });
  const [desktopNewsDetail, setDesktopNewsDetail] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [selectedSponsorId, setSelectedSponsorId] = useState(null);
  const [sponsorClosing, setSponsorClosing] = useState(false);
  const lastSponsorRef = useRef(null);
  const [featureLocks, setFeatureLocks] = useState({});

  // --- Availability state ---
  const [availGroups, setAvailGroups] = useState([]);
  const [availLoading, setAvailLoading] = useState(true);
  const [availSaving, setAvailSaving] = useState(null); // group key being saved
  const [pendingVoteCount, setPendingVoteCount] = useState(0);
  const [bfOpenTrigger, setBfOpenTrigger] = useState(0);
  const availSectionRef = useRef(null);

  // Horizontal carousel for the Upcoming Events strip. We hold the
  // scroll container ref so left/right chevrons can scroll by one
  // card width at a time, and track whether we can scroll further so
  // the arrows auto-hide at either edge.
  const eventsScrollRef = useRef(null);
  const [eventsEdge, setEventsEdge] = useState({ left: true, right: false });
  // Same treatment for the News "From the club" strip below.
  const newsScrollRef = useRef(null);
  const [newsEdge, setNewsEdge] = useState({ left: true, right: false });
  const updateEventsEdge = () => {
    const el = eventsScrollRef.current;
    if (!el) return;
    setEventsEdge({
      left: el.scrollLeft <= 2,
      right: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2,
    });
  };
  const updateNewsEdge = () => {
    const el = newsScrollRef.current;
    if (!el) return;
    setNewsEdge({
      left: el.scrollLeft <= 2,
      right: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2,
    });
  };
  const scrollEvents = (dir) => {
    const el = eventsScrollRef.current;
    if (!el) return;
    const amount = Math.max(280, Math.min(el.clientWidth * 0.9, 480));
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };
  const scrollNews = (dir) => {
    const el = newsScrollRef.current;
    if (!el) return;
    const amount = Math.max(280, Math.min(el.clientWidth * 0.9, 480));
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };
  useEffect(() => {
    // Recompute edges whenever the event list changes or on mount.
    const el = eventsScrollRef.current;
    if (!el) return;
    updateEventsEdge();
    el.addEventListener("scroll", updateEventsEdge, { passive: true });
    const ro = new ResizeObserver(updateEventsEdge);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateEventsEdge);
      ro.disconnect();
    };
  }, [events.length]);
  useEffect(() => {
    const el = newsScrollRef.current;
    if (!el) return;
    updateNewsEdge();
    el.addEventListener("scroll", updateNewsEdge, { passive: true });
    const ro = new ResizeObserver(updateNewsEdge);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateNewsEdge);
      ro.disconnect();
    };
  }, [news.length]);

  // Desktop events rail edge tracking
  const updateDesktopEventsEdge = () => {
    const el = desktopEventsScrollRef.current;
    if (!el) return;
    setDesktopEventsEdge({
      left: el.scrollLeft <= 2,
      right: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2,
    });
  };
  const scrollDesktopEvents = (dir) => {
    const el = desktopEventsScrollRef.current;
    if (!el) return;
    const step = Math.max(280, Math.floor(el.clientWidth * 0.85));
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };
  useEffect(() => {
    const el = desktopEventsScrollRef.current;
    if (!el) return;
    updateDesktopEventsEdge();
    el.addEventListener("scroll", updateDesktopEventsEdge, { passive: true });
    const ro = new ResizeObserver(updateDesktopEventsEdge);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateDesktopEventsEdge);
      ro.disconnect();
    };
  }, [events.length]);

  const loadDashboardNotifs = async () => {
    try {
      const [c, mn] = await Promise.all([
        api.get("/chat/unread-with-rooms"),
        api.get("/notifications/member"),
      ]);
      setChatNotifs(c.data || []);
      setMemberNotifs(Array.isArray(mn.data) ? mn.data : []);
    } catch (err) { console.warn("[Dashboard] Failed to load dashboard notifs:", err); }
  };

  const dismissMemberNotif = async (notifId) => {
    try {
      await api.put(`/notifications/member/${notifId}/read`);
      setMemberNotifs((prev) => prev.filter((n) => n.id !== notifId));
    } catch { /* silent */ }
  };

  // --- Availability helpers ---
  const loadAvailability = async () => {
    try {
      const { data } = await api.get("/fixtures/pending-availability");
      setAvailGroups(data?.groups || []);
    } catch (err) {
      console.warn("[Dashboard] loadAvailability:", err);
    } finally {
      setAvailLoading(false);
    }
  };

  const submitAvailability = async (group, status) => {
    if (availSaving) return;
    setAvailSaving(group.key);
    try {
      await api.post("/fixtures/availability/batch", {
        fixture_ids: group.fixture_ids,
        status,
      });
      // Optimistic update
      setAvailGroups((prev) =>
        prev.map((g) =>
          g.key === group.key
            ? { ...g, my_status: status, pending_count: 0, all_responded: true }
            : g
        )
      );
      toast.success(
        status === "available"
          ? "Marked as available"
          : status === "reserve"
          ? "Marked as reserve"
          : "Marked as unavailable"
      );
      window.dispatchEvent(new CustomEvent("dcc:availability-changed"));
    } catch (err) {
      console.warn("[Dashboard] submitAvailability:", err);
      toast.error(err?.response?.data?.detail || "Could not update availability");
    } finally {
      setAvailSaving(null);
    }
  };

  const dismissChatNotif = async (roomId) => {
    try {
      await api.post(`/chat/ack/${roomId}`);
    } catch (err) { console.warn("[Dashboard] Failed to ack chat:", err); }
    setChatNotifs((arr) => arr.filter((c) => c.room_id !== roomId));
    navigate("/m/chat");
  };

  const submitRsvp = async (eventId, status) => {
    // If user taps their current status again, clear it (i.e. toggle off)
    const current = events.find((e) => e.id === eventId)?.my_rsvp;
    const willClear = current === status;
    setRsvpSaving(true);
    try {
      if (willClear) {
        await api.delete(`/events/${eventId}/rsvp`);
      } else {
        await api.post(`/events/${eventId}/rsvp`, { status });
      }
      // Update local state for both the list and the expanded modal
      setEvents((arr) => arr.map((e) => {
        if (e.id !== eventId) return e;
        const wasAttending = e.my_rsvp === "attending";
        const willBeAttending = !willClear && status === "attending";
        let count = e.attending_count || 0;
        if (wasAttending && !willBeAttending) count = Math.max(0, count - 1);
        if (!wasAttending && willBeAttending) count = count + 1;
        return { ...e, my_rsvp: willClear ? null : status, attending_count: count };
      }));
      setExpandedEvent((cur) => {
        if (!cur || cur.id !== eventId) return cur;
        const wasAttending = cur.my_rsvp === "attending";
        const willBeAttending = !willClear && status === "attending";
        let count = cur.attending_count || 0;
        if (wasAttending && !willBeAttending) count = Math.max(0, count - 1);
        if (!wasAttending && willBeAttending) count = count + 1;
        return { ...cur, my_rsvp: willClear ? null : status, attending_count: count };
      });
      toast.success(willClear ? "RSVP cleared" : (status === "attending" ? "You're attending — thanks!" : "RSVP saved"));
      // Trigger sidebar count refresh
      window.dispatchEvent(new CustomEvent("dcc:events-rsvp-changed"));
    } catch (err) {
      console.warn("[Dashboard] RSVP failed:", err);
      toast.error("Could not save RSVP");
    } finally {
      setRsvpSaving(false);
    }
  };

  const loadUnreadComments = async () => {
    try {
      const { data } = await api.get("/fixtures/comments/unread");
      setUnreadComments(data || { total: 0, per_fixture: {} });
    } catch (err) { console.warn("[Dashboard] loadUnreadComments:", err); }
  };

  const loadCommentsFor = async (fixtureId) => {
    try {
      const { data } = await api.get(`/fixtures/${fixtureId}/comments`);
      setCommentsByFixture((m) => ({ ...m, [fixtureId]: data || [] }));
    } catch (err) { console.warn("[Dashboard] loadCommentsFor:", err); }
  };

  const markFixtureCommentsRead = async (fixtureId) => {
    try {
      await api.post(`/fixtures/${fixtureId}/comments/read`);
      setUnreadComments((u) => {
        const per = { ...(u.per_fixture || {}) };
        const wasUnread = per[fixtureId] || 0;
        delete per[fixtureId];
        return { total: Math.max(0, (u.total || 0) - wasUnread), per_fixture: per };
      });
    } catch (err) { console.warn("[Dashboard] markFixtureCommentsRead:", err); }
  };

  const appendEmojiToDraft = (key, emoji) => {
    setCommentDraft((d) => ({ ...d, [key]: (d[key] || "") + emoji }));
  };

  const toggleCommentReaction = async (fixtureId, commentId, emoji) => {
    try {
      await api.post(`/fixtures/comments/${commentId}/reactions`, { emoji });
      // Optimistic refresh of that fixture's comments
      const { data } = await api.get(`/fixtures/${fixtureId}/comments`);
      setCommentsByFixture((m) => ({ ...m, [fixtureId]: data || [] }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not react");
    }
  };

  const submitComment = async (fixtureId, parentId = null) => {
    const draftKey = parentId ? `${fixtureId}:${parentId}` : fixtureId;    const text = (commentDraft[draftKey] || "").trim();
    if (!text) return;
    setSendingComment(draftKey);
    try {
      const { data } = await api.post(`/fixtures/${fixtureId}/comments`, { text, parent_id: parentId });
      setCommentsByFixture((m) => ({ ...m, [fixtureId]: [...(m[fixtureId] || []), data] }));
      setCommentDraft((d) => ({ ...d, [draftKey]: "" }));
      if (parentId) setReplyTo((r) => ({ ...r, [fixtureId]: null }));
      window.dispatchEvent(new CustomEvent("dcc:comments-updated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post comment");
    } finally {
      setSendingComment(null);
    }
  };

  useEffect(() => {
    api.get("/events", { params: { upcoming: true } }).then(({ data }) => setEvents(data || []));
    api.get("/news").then(({ data }) => setNews(data || []));
    loadUnreadComments();
    loadDashboardNotifs();
    loadAvailability();
    // Landing-style desktop sections: fetch public stats summary + landing settings
    api.get("/public/stats/summary").then(({ data }) => setPublicStats(data || null)).catch(() => {});
    api.get("/landing").then(({ data }) => setLanding(data || null)).catch(() => {});
    api.get("/sponsors/public").then(({ data }) => setSponsors(data || [])).catch(() => {});
    api.get("/feature-locks").then(({ data }) => setFeatureLocks(data || {})).catch(() => {});
    const handlerComments = () => { loadUnreadComments(); loadDashboardNotifs(); };
    window.addEventListener("dcc:comments-updated", handlerComments);
    // Refresh chat/pickup notifs periodically
    const t = setInterval(loadDashboardNotifs, 30000);
    return () => {
      window.removeEventListener("dcc:comments-updated", handlerComments);
      clearInterval(t);
    };
  }, [user?.id]);

  // Deep-link: auto-open news detail dialog from ?news=<id> query param (e.g. push notification tap)
  useEffect(() => {
    if (!news || news.length === 0) return;
    const newsId = searchParams.get("news");
    if (!newsId) return;
    const target = news.find((n) => n.id === newsId);
    if (target) {
      setDesktopNewsDetail(target);
      // Clear the param so closing the dialog doesn't re-open it
      setSearchParams((prev) => { prev.delete("news"); return prev; }, { replace: true });
    }
  }, [news, searchParams, setSearchParams]);

  // Lock body scroll when sponsor overlay is open
  useEffect(() => {
    if (selectedSponsorId) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [selectedSponsorId]);

  const copyId = () => { navigator.clipboard.writeText(user?.member_id || ""); toast.success("Member ID copied"); };

  return (
    <AppShell variant="member" title={`Welcome back, ${user?.first_name || "Member"}`}>

      {/* PWA install banner — slim bar at the very top, only inside /m */}
      <InstallAppCard />

      {/* PWA update banner — shown only in standalone/installed mode when a new SW is waiting */}
      <UpdateBanner />

      {/* Android APK update banner — shown when a newer APK version is available on the server */}
      <ApkUpdateBanner />

      {/* Quick actions — mirrors member sidebar pages */}
      <section className="mb-8" data-testid="dashboard-quick-actions">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Quick actions</div>
            <h2 className="mt-2 text-2xl font-bold tracking-tight">Jump to</h2>
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-3">
          {[
            ...(isAdmin ? [{ to: "/a", icon: Shield, label: "Admin Portal", desc: "Manage the club", lockKey: null }] : []),
            { to: "/m/fixtures", icon: Trophy, label: "Fixtures", desc: "Upcoming matches", lockKey: "member_fixtures" },
            { to: "/m/playhq", icon: Cloud, label: "Results", desc: "Match results", lockKey: "member_results" },
            { to: "/m/stats", icon: BarChart3, label: "Stats", desc: "Player statistics", lockKey: "member_stats" },
            { to: "/m/events", icon: Calendar, label: "Events", desc: "Club events", lockKey: "member_events" },
            { to: "/m/gallery", icon: ImageIco, label: "Gallery", desc: "Club photos", lockKey: "member_gallery" },
            { to: "/m/chat", icon: MessagesSquare, label: "Chat", desc: "Group chats", lockKey: "member_chat" },
            { to: "/m/loans", icon: Package, label: "On Loan", desc: "Equipment loans", lockKey: "member_on_loan" },
            { to: "/m/profile", icon: UserIcon, label: "Profile", desc: "Your details", lockKey: "member_profile" },
          ].filter((q) => !q.lockKey || isPrimaryAdmin || featureLocks[q.lockKey] !== false).map((q, i) => (
            <Link key={q.to} to={q.to} data-testid={`member-quick-${q.label}`} className="group">
              <Card className="p-2.5 h-full rounded-xl hover:border-foreground/40 transition-all hover-lift bg-[#faf8f5]">
                <div className="flex items-start justify-between">
                  <div className="h-7 w-7 rounded-md bg-[hsl(var(--secondary))] text-[hsl(var(--accent))] flex items-center justify-center group-hover:bg-[hsl(var(--accent))] group-hover:text-white transition-colors">
                    <q.icon className="h-3.5 w-3.5" />
                  </div>
                  <ArrowUpRight className="h-3 w-3 text-muted-foreground group-hover:text-[hsl(var(--accent))] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
                <div className="mt-2 font-semibold tracking-tight text-xs">{q.label}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{q.desc}</div>
              </Card>
            </Link>
          ))}
          {/* Push notification tile — appears in grid when subscribed */}
          <PushNotificationCard variant="tile" />
        </div>
      </section>
      {(chatNotifs.length > 0 || memberNotifs.length > 0) && (
        <section className="mb-3 space-y-3" data-testid="dashboard-notifications">
          {memberNotifs.map((n) => (
            <Card key={n.id} className="p-4 border-l-4 border-l-[hsl(var(--accent))] flex items-start gap-3" data-testid={`member-notif-${n.id}`}>
              <div className="h-9 w-9 rounded-lg bg-[hsl(var(--accent))]/10 flex items-center justify-center shrink-0 mt-0.5">
                <UsersIcon className="h-4 w-4 text-[hsl(var(--accent))]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{n.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>
                {n.grade && <Badge variant="outline" className="mt-1.5 text-[10px]">{n.grade}</Badge>}
              </div>
              <button
                onClick={() => dismissMemberNotif(n.id)}
                className="shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                aria-label="Dismiss notification"
                data-testid={`dismiss-notif-${n.id}`}
              >
                <XIcon className="h-3.5 w-3.5" />
              </button>
            </Card>
          ))}
          {chatNotifs.map((c) => (
            <Card
              key={c.room_id}
              className="p-4 border-l-4 border-l-blue-500 cursor-pointer hover:bg-muted/40 transition-colors"
              onClick={() => dismissChatNotif(c.room_id)}
              data-testid={`chat-unread-${c.room_id}`}
            >
              <div className="font-semibold text-sm">{c.room_name || "Chat"}</div>
              {c.snippet && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.author_name ? `${c.author_name}: ` : ""}{c.snippet}</div>}
            </Card>
          ))}
        </section>
      )}

      {/* B&F VOTING — hidden visually when availability-blocked card is showing (avoids duplicate prompt),
           but stays mounted so the voting dialog can still open from the "Enter votes now" button */}
      <div className={pendingVoteCount > 0 ? "hidden" : "mb-3"}>
        <BFVotingSection
          onPendingChange={setPendingVoteCount}
          returnToRef={availSectionRef}
          openTrigger={bfOpenTrigger}
        />
      </div>

      {/* ---- PUSH NOTIFICATIONS ---- */}
      <section className="mb-8" data-testid="dashboard-push">
        <PushNotificationCard />
      </section>

      {/* ---- AVAILABILITY SECTION ---- */}
      {!availLoading && availGroups.length > 0 && (
        <section className="mb-8" data-testid="dashboard-availability" ref={availSectionRef}>
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-2">
                <ClipboardList className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> Availability
              </div>
              <h2 className="mt-2 text-2xl font-bold tracking-tight">Select your availability</h2>
            </div>
          </div>

          {/* Block if pending votes */}
          {pendingVoteCount > 0 ? (
            <Card className="p-4 border border-amber-500/30 bg-amber-500/5 rounded-xl" data-testid="availability-blocked-by-votes">
              <div className="flex items-start gap-3">
                <Trophy className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground">Enter your B&F votes first</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    You have {pendingVoteCount} outstanding B&F vote{pendingVoteCount !== 1 ? "s" : ""} to enter before you can set your availability.
                  </div>
                  <Button
                    size="sm"
                    className="mt-3 h-8 px-4 text-xs font-semibold rounded-lg"
                    onClick={() => setBfOpenTrigger((n) => n + 1)}
                    data-testid="availability-enter-votes-btn"
                  >
                    <Trophy className="h-3.5 w-3.5 mr-1.5" />
                    Enter votes now
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <div className="space-y-4">
              {availGroups.map((group) => {
                const isSaving = availSaving === group.key;
                const dateLabel = group.date
                  ? new Date(group.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })
                  : "Date TBC";
                const hasResponded = group.my_status && group.my_status !== "mixed";
                return (
                  <AvailabilityGroupCard
                    key={group.key}
                    group={group}
                    dateLabel={dateLabel}
                    hasResponded={hasResponded}
                    isSaving={isSaving}
                    onSubmit={submitAvailability}
                  />
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* EVENTS — old mobile layout (replaced by landing-style sections below) */}

      {/* EVENTS — old mobile layout (replaced by landing-style sections below) */}
      <section className="hidden" data-testid="dashboard-events">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> Events
            </div>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight">Upcoming at the club</h2>
          </div>
        </div>
        {events.length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground border-dashed rounded-2xl">
            <Calendar className="mx-auto h-7 w-7 mb-3 opacity-60" />
            No upcoming events. Check back soon.
          </Card>
        ) : (
          /* Horizontal swipe-able strip. The scroll container snaps to
              each card, and chevron buttons on either side scroll by
              roughly one card width. Arrows fade out when the user has
              reached either edge. */
          <div className="relative group" data-testid="dashboard-events-carousel">
            {!eventsEdge.left && (
              <button
                type="button"
                onClick={() => scrollEvents(-1)}
                className="hidden sm:inline-flex absolute left-1 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-background/95 border border-border shadow-lg hover:bg-background text-foreground"
                aria-label="Previous event"
                data-testid="dashboard-events-prev"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            {!eventsEdge.right && (
              <button
                type="button"
                onClick={() => scrollEvents(1)}
                className="hidden sm:inline-flex absolute right-1 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-background/95 border border-border shadow-lg hover:bg-background text-foreground"
                aria-label="Next event"
                data-testid="dashboard-events-next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
            <div
              ref={eventsScrollRef}
              className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-3 -mx-1 px-1 scrollbar-hide"
              style={{ scrollbarWidth: "none" }}
              data-testid="dashboard-events-scroll"
            >
            {events.map((ev) => {
              const showImage = eventCards.isExpanded(ev.id);
              const openDetail = () => setExpandedEvent(ev);
              const onKeyDown = (e) => {
                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDetail(); }
              };
              return (
                <div
                  role="button"
                  tabIndex={0}
                  key={ev.id}
                  onClick={openDetail}
                  onKeyDown={onKeyDown}
                  className="snap-start shrink-0 w-[85%] sm:w-[420px] md:w-[460px] text-left group/card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-2xl cursor-pointer block"
                  data-testid={`dashboard-event-${ev.id}`}
                >
                  <Card className="h-full overflow-hidden rounded-2xl hover-lift transition-all group-hover/card:border-[hsl(var(--accent))]/40">
                    {/* Full-width poster reveals only when toggled on. */}
                    {showImage && ev.image_url && (
                      <div className="aspect-[21/9] max-h-[420px] bg-[hsl(var(--secondary))] overflow-hidden relative">
                        <img src={ev.image_url} alt={ev.title} className="w-full h-full object-cover" />
                        {ev.my_rsvp === "attending" && (
                          <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/95 text-white text-[10px] font-semibold shadow-md backdrop-blur" data-testid={`event-card-attending-badge-${ev.id}`}>
                            <Check className="h-3 w-3" /> Attending
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex items-stretch gap-4 p-4 relative">
                      {!showImage && (<CardThumb
                        src={ev.image_url}
                        alt={ev.title}
                        className="h-14 w-20 sm:h-16 sm:w-24 md:h-32 md:w-48 rounded-lg"
                      />)}
                      <div className="flex-1 min-w-0 flex items-center">
                        <div>
                          <div className="text-base font-semibold tracking-tight">
                            {ev.title}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              );
            })}
            </div>
            {/* Edge-fade hints the user there's more to swipe to */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-background to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent" />
          </div>
        )}
      </section>

      {/* EVENT DETAIL DIALOG — matches landing page exactly */}
      <Dialog open={!!expandedEvent} onOpenChange={(v) => { if (!v) setExpandedEvent(null); }}>
        <DialogContent
          className="p-0 w-[98vw] max-w-[98vw] sm:w-auto sm:max-w-[80vw] max-h-[98dvh] overflow-hidden rounded-xl border-0 shadow-2xl bg-white text-foreground [&>button]:hidden"
          data-testid="event-detail-dialog"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{expandedEvent?.title || "Event"}</DialogTitle>
          </DialogHeader>
          {expandedEvent && (
            <MemberEventDetail
              ev={expandedEvent}
              onClose={() => setExpandedEvent(null)}
              rsvpSaving={rsvpSaving}
              submitRsvp={submitRsvp}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* DESKTOP NEWS DETAIL DIALOG — matches landing page exactly */}
      <Dialog open={!!desktopNewsDetail} onOpenChange={(v) => { if (!v) setDesktopNewsDetail(null); }}>
        <DialogContent
          className="p-0 w-[98vw] max-w-[98vw] sm:w-auto sm:max-w-[80vw] max-h-[98dvh] overflow-hidden rounded-xl border-0 shadow-2xl bg-white text-foreground [&>button]:hidden"
          data-testid="desktop-news-detail-dialog"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{desktopNewsDetail?.title || "News article"}</DialogTitle>
          </DialogHeader>
          {desktopNewsDetail && (
            <MemberNewsDetail
              item={desktopNewsDetail}
              onClose={() => setDesktopNewsDetail(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* NEWS — old mobile layout (replaced by landing-style sections below) */}
      <section data-testid="dashboard-news" className="hidden">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-2">
              <Newspaper className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> News
            </div>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight">From the club</h2>
          </div>
        </div>

        {news.length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground border-dashed rounded-2xl">
            <Newspaper className="mx-auto h-7 w-7 mb-3 opacity-60" />
            No news yet. Stay tuned!
          </Card>
        ) : (
          /* Horizontal swipe-able strip — mirrors the Events carousel
              above so admins get a consistent swipe pattern across
              both feeds. */
          <div className="relative group" data-testid="dashboard-news-carousel">
            {!newsEdge.left && (
              <button
                type="button"
                onClick={() => scrollNews(-1)}
                className="hidden sm:inline-flex absolute left-1 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-background/95 border border-border shadow-lg hover:bg-background text-foreground"
                aria-label="Previous news"
                data-testid="dashboard-news-prev"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            {!newsEdge.right && (
              <button
                type="button"
                onClick={() => scrollNews(1)}
                className="hidden sm:inline-flex absolute right-1 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-background/95 border border-border shadow-lg hover:bg-background text-foreground"
                aria-label="Next news"
                data-testid="dashboard-news-next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
            <div
              ref={newsScrollRef}
              className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-3 -mx-1 px-1 scrollbar-hide"
              style={{ scrollbarWidth: "none" }}
              data-testid="dashboard-news-scroll"
            >
            {news.map((n) => {
              const isNewsExpanded = expandedNewsCards.has(n.id);
              const toggleNewsExpand = (e) => {
                e.stopPropagation();
                setExpandedNewsCards((prev) => {
                  const next = new Set(prev);
                  next.has(n.id) ? next.delete(n.id) : next.add(n.id);
                  return next;
                });
              };
              return (
                <div
                  role="button"
                  tabIndex={0}
                  key={n.id}
                  onClick={toggleNewsExpand}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleNewsExpand(e); } }}
                  className="snap-start shrink-0 w-[85%] sm:w-[420px] md:w-[460px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-2xl cursor-pointer block"
                  data-testid={`dashboard-news-${n.id}`}
                >
                  <Card className="h-full overflow-hidden rounded-2xl hover-lift transition-all">
                    {/* Full-width image reveals only when expanded */}
                    {isNewsExpanded && n.image_url && (
                      <div className="aspect-[21/9] max-h-[420px] bg-[hsl(var(--secondary))] overflow-hidden">
                        <img src={n.image_url} alt={n.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex items-stretch gap-4 p-4 relative">
                      {!isNewsExpanded && (<CardThumb
                        src={n.image_url}
                        alt={n.title}
                        className="h-14 w-20 sm:h-16 sm:w-24 md:h-32 md:w-48 rounded-lg"
                      />)}
                      <div className="flex-1 min-w-0">
                        <div className="text-base font-semibold tracking-tight">{n.title}</div>
                        {isNewsExpanded && (
                          <>
                            <div className="text-[10px] font-mono-sm uppercase tracking-[0.18em] text-muted-foreground mt-1">
                              {fmtDate(n.published_at)} {n.author ? ` · ${n.author}` : ""}
                            </div>
                            {(n.summary || n.body) && (
                              <p className="mt-2 text-sm text-muted-foreground line-clamp-4 whitespace-pre-wrap">{n.summary || n.body}</p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </Card>
                </div>
              );
            })}
            </div>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-background to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent" />
          </div>
        )}
      </section>

      {/* SEASON STATS — old mobile layout (replaced by landing-style sections below) */}
      <section className="hidden" data-testid="dashboard-stats-board">
        <StatsBoard align="left" />
      </section>

      {/* PLAYHQ MATCHES — old mobile layout (replaced by landing-style sections below) */}
      <section className="hidden" data-testid="dashboard-playhq-matches">
        <PlayHQPreview align="left" hideCompleted />
      </section>


      {/* ========== LANDING-STYLE SECTIONS (all viewports) ========== */}
      <div className="-mx-4 sm:-mx-6 md:-mx-8 lg:-mx-10" data-testid="dashboard-desktop-landing-sections">

        {/* SPONSORS — marquee showcase (same as landing page) */}
        {sponsors.length > 0 && landing?.sponsors_section_visible !== false && (() => {
          const track = [...sponsors, ...sponsors, ...sponsors];
          const trackSponsorClick = (id) => { try { api.post(`/sponsors/${id}/click`).catch(() => {}); } catch (e) { console.error("[Dashboard] sponsor click track:", e); } };
          const selected = selectedSponsorId ? sponsors.find((s) => s.id === selectedSponsorId) : null;
          if (selected) lastSponsorRef.current = selected;
          const displaySponsor = sponsorClosing ? lastSponsorRef.current : selected;
          const showOverlay = !!displaySponsor;

          const closeSponsor = () => {
            setSponsorClosing(true);
            setTimeout(() => {
              setSelectedSponsorId(null);
              setSponsorClosing(false);
            }, 200);
          };
          return (
            <section className="border-b border-border" data-testid="member-sponsors-section">
              <div
                className="relative overflow-hidden w-full"
                style={{ maxWidth: "1920px", height: "138px", margin: "0 auto" }}
                data-testid="member-sponsors-marquee"
              >
                <div
                  className={`anim-sponsor-marquee flex items-center gap-8 sm:gap-12 w-max h-full bg-white ${selectedSponsorId ? "marquee-paused" : ""}`}
                  style={{ animationDuration: `${landing?.sponsors_carousel_speed ?? 40}s` }}
                >
                  {track.map((s, i) => {
                    const realIdx = i % sponsors.length;
                    return (
                      <button
                        key={`${s.id}-${i}`}
                        type="button"
                        onClick={() => { trackSponsorClick(s.id); setSelectedSponsorId(selectedSponsorId === s.id ? null : s.id); }}
                        className={`shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))] cursor-pointer ${selectedSponsorId === s.id ? "ring-2 ring-emerald-500" : ""}`}
                        aria-label={`View sponsor ${s.name}`}
                        data-testid={`member-sponsor-${s.id}`}
                      >
                        <div className="relative flex items-center justify-center rounded-sm overflow-hidden transition-transform duration-200 ease-out hover:scale-[1.05] hover:z-10" style={{ width: 208, height: 110 }}>
                          {s.logo_url ? (
                            <img src={s.logo_url} alt={s.name} className="w-full h-full object-contain" loading="lazy" />
                          ) : (
                            <div className="text-xs font-semibold text-foreground text-center px-2 line-clamp-2">{s.name}</div>
                          )}
                          {(() => {
                            const badge = MEMBER_TIER_BADGES[(s.tier || "").trim().toLowerCase()];
                            if (!badge) return null;
                            return (
                              <span
                                className={`absolute top-1 left-1 px-1.5 py-[1px] rounded-sm text-[8px] font-bold uppercase tracking-wider leading-tight border ${badge.bg} ${badge.text} ${badge.border} pointer-events-none select-none`}
                                data-testid={`member-sponsor-tier-badge-${s.id}`}
                              >
                                {badge.label}
                              </span>
                            );
                          })()}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Sponsor detail overlay */}
              {showOverlay && (
                <>
                  <div
                    className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ease-out ${sponsorClosing ? 'opacity-0' : 'opacity-100'}`}
                    onClick={closeSponsor}
                    data-testid="member-sponsor-detail-backdrop"
                  />
                  <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200 ease-out ${sponsorClosing ? 'opacity-0' : 'opacity-100'}`} onClick={closeSponsor}>
                    <div
                      className={`bg-background rounded-xl shadow-2xl border border-border max-w-lg w-full max-h-[90vh] overflow-y-auto scrollbar-hide p-6 sm:p-8 relative cursor-pointer transition-all duration-200 ease-out ${sponsorClosing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
                      onClick={(e) => { if (window.innerWidth >= 640) e.stopPropagation(); }}
                      data-testid={`member-sponsor-detail-${displaySponsor.id}`}
                    >
                      <button
                        type="button"
                        onClick={closeSponsor}
                        className="absolute top-3 right-3 h-8 w-8 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors sm:hidden"
                        aria-label="Close"
                        data-testid="member-sponsor-detail-close"
                      >
                        <XIcon className="h-5 w-5" />
                      </button>
                      {displaySponsor.logo_url && (
                        <div className="flex justify-center mb-4">
                          <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-lg bg-white border border-border flex items-center justify-center p-1 overflow-hidden">
                            <img src={displaySponsor.logo_url} alt={displaySponsor.name} className="max-h-full max-w-full object-contain" />
                          </div>
                        </div>
                      )}
                      <div>
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          <div className="font-bold text-lg sm:text-xl">{displaySponsor.name}</div>
                          {(() => {
                            const badge = MEMBER_TIER_BADGES[(displaySponsor.tier || "").trim().toLowerCase()];
                            if (!badge) return null;
                            return (
                              <span
                                className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider leading-tight border ${badge.bg} ${badge.text} ${badge.border}`}
                                data-testid={`member-sponsor-detail-tier-badge-${displaySponsor.id}`}
                              >
                                {badge.label}
                              </span>
                            );
                          })()}
                        </div>
                        {displaySponsor.website_url && (
                          <a
                            href={displaySponsor.website_url}
                            target="_blank"
                            rel="noreferrer noopener sponsored"
                            className="text-sm text-[#4169E1] hover:underline break-all"
                            data-testid={`member-sponsor-website-${displaySponsor.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {displaySponsor.website_url}
                          </a>
                        )}
                        {displaySponsor.description && (
                          <p className="mt-3 text-sm text-muted-foreground whitespace-pre-wrap">{displaySponsor.description}</p>
                        )}
                        {!displaySponsor.description && !displaySponsor.website_url && (
                          <p className="mt-2 text-sm text-muted-foreground italic">No details available for this sponsor.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </section>
          );
        })()}

        {/* EVENTS — landing style (matching Landing.jsx layout exactly) */}
        {events.length > 0 && (
          <section className="border-b border-border overflow-x-hidden overflow-y-visible" data-testid="dashboard-desktop-events">
            <div className="mx-auto pt-0 pb-[10px] w-full max-w-[1920px]">
              <div className="flex items-end justify-between gap-4 mb-2 sm:mb-[20px] flex-wrap pl-5 sm:pl-6 lg:pl-[232px] pr-5 sm:pr-6 lg:pr-[232px] py-1.5 sm:py-3 z-10" style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #2749C9 55%, #4169E1 100%)" }}>
                <h2
                  className="text-2xl sm:text-5xl lg:text-6xl tracking-tight uppercase leading-none text-white"
                  style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif", fontWeight: 900 }}
                  data-testid="dashboard-desktop-events-heading"
                >
                  Upcoming events
                </h2>
                <Link
                  to="/m/events"
                  className="inline-flex items-center gap-1.5 text-white text-sm font-semibold hover:text-white/80 active:scale-95 transition-all mb-1"
                  data-testid="dashboard-desktop-all-events"
                >
                  All Events <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              <div className="relative mx-5 sm:mx-6 lg:mx-[232px] overflow-hidden">
                <div
                  ref={desktopEventsScrollRef}
                  className="flex gap-2 sm:gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-3 scrollbar-hide"
                  style={{ scrollbarWidth: "none" }}
                  data-testid="dashboard-desktop-events-scroller"
                >
                  {events.map((ev) => {
                    const isLandscape = ev.image_orientation === "landscape";
                    const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
                    const baseW = isLandscape ? 604 : 340;
                    const mobileW = isLandscape ? Math.min(Math.round(window.innerWidth * 0.8), 380) : Math.min(Math.round(window.innerWidth * 0.72), 300);
                    const w = isMobile ? mobileW : baseW;
                    const scaleRatio = w / baseW;
                    return (
                      <Card
                        key={ev.id}
                        className={`snap-start shrink-0 overflow-hidden rounded-sm border border-neutral-300/90 cursor-pointer transition-[transform,box-shadow] duration-200 ease-out hover:scale-[1.02] hover:-translate-y-2 hover:shadow-[0_20px_44px_-8px_rgba(0,0,0,0.22),0_8px_18px_-4px_rgba(0,0,0,0.12)] hover:z-10 flex flex-col shadow-[0_4px_16px_-4px_rgba(0,0,0,0.15),0_2px_6px_-2px_rgba(0,0,0,0.1),0_0_0_0.5px_rgba(0,0,0,0.04)] ${isLandscape ? "self-center" : "self-start"}`}
                        style={{ width: w, maxWidth: "calc(100vw - 40px)" }}
                        onClick={() => setExpandedEvent(ev)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpandedEvent(ev); } }}
                        data-testid={`dashboard-desktop-event-${ev.id}`}
                      >
                        <div className="relative bg-white overflow-hidden" style={isLandscape ? { height: Math.round(340 * scaleRatio) } : { aspectRatio: "1080 / 1350" }}>
                          {ev.pinned_on_landing && (
                            <div
                              className="absolute top-1.5 left-1.5 sm:top-2.5 sm:left-2.5 z-10 inline-flex items-center gap-0.5 sm:gap-1 rounded-full bg-black/70 backdrop-blur-sm text-white text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.1em] sm:tracking-[0.14em] px-1.5 sm:px-2.5 py-0.5 sm:py-1 shadow-md"
                              data-testid={`dashboard-desktop-event-pinned-${ev.id}`}
                            >
                              <Pin className="h-2.5 w-2.5 sm:h-3 sm:w-3" fill="currentColor" /> Pinned
                            </div>
                          )}
                          {ev.my_rsvp === "attending" && (
                            <div
                              className="absolute top-1.5 right-1.5 sm:top-2.5 sm:right-2.5 z-10 inline-flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-emerald-500/95 text-white text-[9px] sm:text-[10px] font-semibold shadow-md backdrop-blur"
                              data-testid={`dashboard-desktop-event-attending-${ev.id}`}
                            >
                              <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3" /> Attending
                            </div>
                          )}
                          {ev.image_url ? (
                            <img src={ev.image_url} alt={ev.title} className="w-full h-full object-cover block" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-[hsl(var(--secondary))]">
                              <Calendar className="h-6 w-6 sm:h-10 sm:w-10 opacity-50" />
                            </div>
                          )}
                        </div>
                        <div className="px-3 sm:px-4 py-2.5 sm:py-3 shrink-0 bg-[#faf8f5]">
                          <h3 className="text-sm sm:text-base font-bold leading-tight truncate text-black" title={ev.title}>{ev.title}</h3>
                          <div className="mt-1 flex items-center gap-1.5 text-[11px] sm:text-xs text-black/70">
                            <Calendar className="h-3 w-3 shrink-0" />
                            <span>{fmtDateRange(ev.start_at, ev.end_at)}</span>
                          </div>
                          {ev.location && (
                            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] sm:text-xs text-black/60">
                              <MapPin className="h-3 w-3 shrink-0" />
                              <span className="truncate">{ev.location}</span>
                            </div>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
                {!desktopEventsEdge.left && (
                  <button
                    type="button"
                    onClick={() => scrollDesktopEvents(-1)}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 inline-flex h-14 w-11 items-center justify-center text-[hsl(var(--fg))] bg-gradient-to-r from-white/95 via-white/70 to-transparent hover:from-white active:scale-95 transition-all"
                    aria-label="Scroll events left"
                    data-testid="dashboard-desktop-events-scroll-left"
                  >
                    <ChevronLeft className="h-8 w-8" strokeWidth={3} />
                  </button>
                )}
                {!desktopEventsEdge.right && (
                  <button
                    type="button"
                    onClick={() => scrollDesktopEvents(1)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 inline-flex h-14 w-11 items-center justify-center text-[hsl(var(--fg))] bg-gradient-to-l from-white/95 via-white/70 to-transparent hover:from-white active:scale-95 transition-all"
                    aria-label="Scroll events right"
                    data-testid="dashboard-desktop-events-scroll-right"
                  >
                    <ChevronRight className="h-8 w-8" strokeWidth={3} />
                  </button>
                )}
              </div>
            </div>
          </section>
        )}

        {/* NEWS — landing style mosaic (matching Landing.jsx layout exactly) */}
        {news.length > 0 && (
          <section className="border-b border-border" data-testid="dashboard-desktop-news">
            <div className="mx-auto pt-0 pb-[10px] px-5 sm:px-6 lg:px-0 w-full max-w-[1920px]">
              <div className="flex items-end justify-between gap-4 mb-2 sm:mb-[20px] flex-wrap py-1.5 sm:py-3 pl-5 sm:pl-6 lg:pl-[50px] pr-5 sm:pr-6 lg:pr-[232px] -mx-5 sm:-mx-6 lg:ml-[182px] lg:mr-0 z-10" style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #2749C9 55%, #4169E1 100%)" }}>
                <h2
                  className="text-2xl sm:text-5xl lg:text-6xl tracking-tight uppercase leading-none text-white"
                  style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif", fontWeight: 900 }}
                  data-testid="dashboard-desktop-news-heading"
                >
                  Latest news
                </h2>
                <Link
                  to="/news?from=member"
                  className="inline-flex items-center gap-1.5 text-white text-sm font-semibold hover:text-white/80 active:scale-95 transition-all mb-1"
                  data-testid="dashboard-desktop-all-news"
                >
                  All News <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              {(() => {
                const featured = news[0];
                const rest = news.slice(1, 9);
                return (
                  <>
                    {/* Mobile news feed */}
                    <div className="sm:hidden" data-testid="member-news-mobile">
                      {featured && (
                        <article className="cursor-pointer" onClick={() => setDesktopNewsDetail(featured)} role="button" tabIndex={0} data-testid={`member-news-featured-mobile-${featured.id}`}>
                          <div className="relative w-full aspect-[16/10] bg-[hsl(var(--secondary))] overflow-hidden">
                            {featured.image_url ? (
                              <img src={featured.image_url} alt={featured.title} className="w-full h-full object-contain" loading="lazy" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Newspaper className="h-10 w-10 opacity-50" /></div>
                            )}
                          </div>
                          <div className="px-1 pt-3 pb-4">
                            <h3 className="text-xl font-bold tracking-tight leading-snug text-foreground" style={{ fontFamily: "'Inter', sans-serif" }}>{featured.title}</h3>
                            <div className="mt-1.5 text-xs text-muted-foreground">{fmtDate(featured.published_at)}</div>
                          </div>
                        </article>
                      )}
                      <div className="flex flex-col">
                        {rest.map((n) => (
                          <article
                            key={n.id}
                            className="flex gap-3 items-start border-t border-black py-3.5 cursor-pointer active:bg-muted/30 transition-colors"
                            onClick={() => setDesktopNewsDetail(n)}
                            role="button"
                            tabIndex={0}
                            data-testid={`member-news-row-mobile-${n.id}`}
                          >
                            <div className="flex-1 min-w-0 flex flex-col justify-between">
                              <h3 className="text-[15px] font-bold leading-snug text-foreground line-clamp-3" style={{ fontFamily: "'Inter', sans-serif" }}>{n.title}</h3>
                              <div className="mt-2 text-xs text-muted-foreground">{fmtDate(n.published_at)}</div>
                            </div>
                            {n.image_url ? (
                              <div className="shrink-0 w-[clamp(72px,25vw,100px)] h-[clamp(72px,25vw,100px)] rounded-md overflow-hidden bg-[hsl(var(--secondary))]">
                                <img src={n.image_url} alt={n.title} className="w-full h-full object-contain" loading="lazy" />
                              </div>
                            ) : (
                              <div className="shrink-0 w-[clamp(72px,25vw,100px)] h-[clamp(72px,25vw,100px)] rounded-md bg-[hsl(var(--secondary))] flex items-center justify-center">
                                <Newspaper className="h-5 w-5 opacity-40" />
                              </div>
                            )}
                          </article>
                        ))}
                      </div>
                    </div>
                    {/* Desktop news mosaic — featured left, 4-col grid right */}
                    <div className="hidden sm:block relative mx-5 sm:mx-6 lg:mx-[232px] overflow-hidden" data-testid="member-news-mosaic">
                      <div className="flex items-start gap-3">
                        {/* Featured card */}
                        <Card
                          className="shrink-0 overflow-hidden rounded-sm border border-neutral-300/90 cursor-pointer transition-[transform,box-shadow] duration-200 ease-out hover:scale-[1.02] hover:-translate-y-2 hover:shadow-[0_20px_44px_-8px_rgba(0,0,0,0.22),0_8px_18px_-4px_rgba(0,0,0,0.12)] hover:z-10 flex flex-col shadow-[0_4px_16px_-4px_rgba(0,0,0,0.15),0_2px_6px_-2px_rgba(0,0,0,0.1),0_0_0_0.5px_rgba(0,0,0,0.04)]"
                          style={{ width: 340 }}
                          role="button"
                          tabIndex={0}
                          onClick={() => setDesktopNewsDetail(featured)}
                          data-testid={`dashboard-desktop-news-featured-${featured.id}`}
                        >
                          <div className="relative bg-white w-full" style={{ aspectRatio: "1080 / 1350" }}>
                            {featured.image_url ? (
                              <img src={featured.image_url} alt={featured.title} className="w-full h-full object-contain block" loading="lazy" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-[hsl(var(--secondary))]"><Newspaper className="h-10 w-10 opacity-50" /></div>
                            )}
                          </div>
                          <div className="px-4 py-3 bg-[#faf8f5] shrink-0">
                            <h3 className="text-base font-bold leading-tight truncate text-black" title={featured.title}>{featured.title}</h3>
                            <div className="mt-1 flex items-center gap-1.5 text-xs text-black/70">
                              <Calendar className="h-3 w-3 shrink-0" /><span>{fmtDate(featured.published_at)}</span>
                            </div>
                            {featured.summary && <p className="mt-1.5 text-sm leading-snug text-black/60 line-clamp-3">{featured.summary}</p>}
                          </div>
                        </Card>
                        {/* Side cards — fixed size, flow naturally */}
                        {rest.length > 0 && (
                          <div className="flex-1 min-w-0 flex flex-wrap gap-3 content-start">
                            {rest.map((n) => (
                              <Card
                                key={n.id}
                                className="overflow-hidden rounded-sm border border-neutral-300/90 cursor-pointer transition-[transform,box-shadow] duration-200 ease-out hover:scale-[1.02] hover:-translate-y-2 hover:shadow-[0_20px_44px_-8px_rgba(0,0,0,0.22),0_8px_18px_-4px_rgba(0,0,0,0.12)] hover:z-10 flex flex-col shadow-[0_4px_16px_-4px_rgba(0,0,0,0.15),0_2px_6px_-2px_rgba(0,0,0,0.1),0_0_0_0.5px_rgba(0,0,0,0.04)]"
                                style={{ width: 140 }}
                                role="button"
                                tabIndex={0}
                                onClick={() => setDesktopNewsDetail(n)}
                                data-testid={`dashboard-desktop-news-side-${n.id}`}
                              >
                                <div className="relative bg-white w-full" style={{ aspectRatio: n.image_orientation === "landscape" ? "1200 / 630" : "1080 / 1350" }}>
                                  {n.image_url ? (
                                    <img src={n.image_url} alt={n.title} className="absolute inset-0 w-full h-full object-contain block" loading="lazy" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-[hsl(var(--secondary))]"><Newspaper className="h-5 w-5 opacity-50" /></div>
                                  )}
                                </div>
                                <div className="px-2 pt-3 pb-1.5 shrink-0 bg-[#faf8f5]">
                                  <h3 className="text-xs font-bold leading-tight truncate text-black" title={n.title}>{n.title}</h3>
                                  <div className="mt-0.5 flex items-center gap-1 text-[9px] text-black/70">
                                    <Calendar className="h-2.5 w-2.5 shrink-0" /><span>{fmtDate(n.published_at)}</span>
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </section>
        )}

        {/* STATS BOARD — landing style */}
        <section data-testid="dashboard-desktop-stats-board">
          <StatsBoard />
        </section>

        {/* PLAYHQ MATCHES — landing style */}
        <section data-testid="dashboard-desktop-playhq-matches">
          <PlayHQPreview hideCompleted />
        </section>

        {/* SEASON STATS — top batters/bowlers leaderboard (from landing page) */}
        {publicStats && publicStats.matches_played > 0 && (
          <section className="border-b border-border bg-[hsl(var(--secondary))]/30" data-testid="dashboard-desktop-season-stats">
            <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 py-14 sm:py-20">
              <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Season on the pitch</div>
                  <h2 className="mt-3 text-3xl font-bold tracking-tight">
                    {publicStats.season} <span className="font-serif-display text-[hsl(var(--accent))]">leaderboards</span>
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                    Live season snapshot — top run-scorers, wicket-takers and the club's overall record.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="px-3 py-1.5 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5 bg-background text-foreground border-border">
                    <span className="opacity-70">Played</span> <span className="tabular-nums text-sm">{publicStats.matches_played}</span>
                  </div>
                  <div className="px-3 py-1.5 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5 bg-emerald-600 text-white border-transparent">
                    <span className="opacity-70">Won</span> <span className="tabular-nums text-sm">{publicStats.record.wins}</span>
                  </div>
                  <div className="px-3 py-1.5 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5 bg-red-600 text-white border-transparent">
                    <span className="opacity-70">Lost</span> <span className="tabular-nums text-sm">{publicStats.record.losses}</span>
                  </div>
                  {publicStats.record.draws > 0 && (
                    <div className="px-3 py-1.5 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5 bg-background text-foreground border-border">
                      <span className="opacity-70">Drawn</span> <span className="tabular-nums text-sm">{publicStats.record.draws}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Top batters */}
                <Card className="rounded-2xl overflow-hidden" data-testid="dashboard-desktop-top-batters">
                  <div className="p-5 border-b border-border flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-[hsl(var(--accent))]/15 flex items-center justify-center">
                      <Trophy className="h-4 w-4 text-[hsl(var(--accent))]" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">Top Run-Scorers</div>
                      <div className="text-xs text-muted-foreground">Ranked by runs</div>
                    </div>
                  </div>
                  {(publicStats.top_batters || []).length === 0 ? (
                    <div className="p-10 text-center text-sm text-muted-foreground">No batting stats yet.</div>
                  ) : (
                    <ul>
                      {publicStats.top_batters.map((b, i) => (
                        <li key={b.player_name + i} className="flex items-center gap-4 px-5 py-3 border-b border-border/60 last:border-b-0" data-testid={`dashboard-desktop-top-batter-${i}`}>
                          <span className="w-6 text-center text-xs font-mono text-muted-foreground">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">{b.player_name}</div>
                            <div className="text-[11px] text-muted-foreground">{b.innings} inn · HS {b.highest} · Avg {b.average ?? "—"} · NO {b.not_outs || 0}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold tabular-nums">{b.runs}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">runs</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>

                {/* Top bowlers */}
                <Card className="rounded-2xl overflow-hidden" data-testid="dashboard-desktop-top-bowlers">
                  <div className="p-5 border-b border-border flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-[hsl(var(--fg))]/10 flex items-center justify-center">
                      <Star className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">Top Wicket-Takers</div>
                      <div className="text-xs text-muted-foreground">Ranked by wickets</div>
                    </div>
                  </div>
                  {(publicStats.top_bowlers || []).length === 0 ? (
                    <div className="p-10 text-center text-sm text-muted-foreground">No bowling stats yet.</div>
                  ) : (
                    <ul>
                      {publicStats.top_bowlers.map((b, i) => (
                        <li key={b.player_name + i} className="flex items-center gap-4 px-5 py-3 border-b border-border/60 last:border-b-0" data-testid={`dashboard-desktop-top-bowler-${i}`}>
                          <span className="w-6 text-center text-xs font-mono text-muted-foreground">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">{b.player_name}</div>
                            <div className="text-[11px] text-muted-foreground">{b.innings} inn · {Number(b.overs || 0).toFixed(1)} ov · Avg {b.average ?? "—"} · Econ {b.economy ?? "—"}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold tabular-nums">{b.wickets}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">wkts</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </div>

              <div className="mt-8 text-center">
                <Link to="/m/stats" className="text-sm font-medium inline-flex items-center gap-1.5 underline-offset-4 hover:underline text-[hsl(var(--accent))]" data-testid="dashboard-desktop-stats-members-link">
                  View full stats page <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}
